
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

interface EditManagerRatingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  goalId: string;
  goalTitle: string;
  targetValue?: string;
  managerRating: string;
  onSaveSuccess: (goalId: string) => void;
}

const EditManagerRatingDialog = ({ 
  isOpen, 
  onClose, 
  goalId, 
  goalTitle,
  targetValue,
  managerRating,
  onSaveSuccess 
}: EditManagerRatingDialogProps) => {
  const [comments, setComments] = useState("");
  const [selfComments, setSelfComments] = useState("");
  const [selfRatingValue, setSelfRatingValue] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const managerCommentsRef = useRef<HTMLTextAreaElement>(null);
  const { activeYear } = usePerformanceYear();

  useEffect(() => {
    if (isOpen && goalId) {
      loadAppraisalData();
    }
  }, [isOpen, goalId]);

  useEffect(() => {
    if (isOpen && managerCommentsRef.current) {
      // Delay to ensure the dialog is fully rendered and visible
      const timer = setTimeout(() => {
        if (managerCommentsRef.current) {
          managerCommentsRef.current.focus();
          managerCommentsRef.current.select();
        }
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const loadAppraisalData = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        return;
      }

      const { data: appraisal, error } = await supabase
        .from('performance_appraisals')
        .select('self_comments, manager_comments, self_rating')
        .eq('supervisor_id', user.id)
        .eq('goal_id', goalId)
        .eq('year', activeYear)
        .maybeSingle();

      if (error) {
        console.error("Error loading appraisal data:", error);
        return;
      }

      if (appraisal) {
        setSelfComments(appraisal.self_comments || "");
        setComments(appraisal.manager_comments || "");
        setSelfRatingValue(getRatingLabel(appraisal.self_rating));
      }
    } catch (error) {
      console.error("Error loading appraisal data:", error);
    }
  };

  const getRatingLabel = (rating: number | null): string => {
    if (!rating) return "";
    switch (rating) {
      case 1: return "Far Below Expectations";
      case 2: return "Below Expectations";
      case 3: return "Meets Expectations";
      case 4: return "Exceeds Expectations";
      case 5: return "Far Exceeds Expectations";
      default: return "";
    }
  };

  const getManagerRatingLabel = (rating: string): string => {
    switch (rating) {
      case "far-below-expectations":
        return "Far Below Expectations";
      case "below-expectations":
        return "Below Expectations";
      case "meets-expectations":
        return "Meets Expectations";
      case "exceeds-expectations":
        return "Exceeds Expectations";
      case "far-exceeds-expectations":
        return "Far Exceeds Expectations";
      default:
        return "";
    }
  };

  const handleSave = () => {
    setShowConfirmDialog(true);
  };

  const getManagerRatingValue = (rating: string): number | null => {
    switch (rating) {
      case "far-below-expectations":
        return 1;
      case "below-expectations":
        return 2;
      case "meets-expectations":
        return 3;
      case "exceeds-expectations":
        return 4;
      case "far-exceeds-expectations":
        return 5;
      default:
        return null;
    }
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        setErrorMessage("Authentication required. Please log in and try again.");
        setShowErrorDialog(true);
        setShowConfirmDialog(false);
        return;
      }

      const managerRatingValue = getManagerRatingValue(managerRating);
      
      if (managerRatingValue === null) {
        setErrorMessage("Please select a valid manager rating before saving.");
        setShowErrorDialog(true);
        setShowConfirmDialog(false);
        return;
      }

      // Find the record where user is supervisor and goal_id matches
      const { data: existingAppraisal, error: checkError } = await supabase
        .from('performance_appraisals')
        .select('id')
        .eq('supervisor_id', user.id)
        .eq('goal_id', goalId)
        .eq('year', activeYear)
        .maybeSingle();

      if (checkError) {
        console.error("Error checking existing appraisal:", checkError);
        setErrorMessage("Failed to check existing appraisal data.");
        setShowErrorDialog(true);
        setShowConfirmDialog(false);
        return;
      }

      if (!existingAppraisal) {
        setErrorMessage("No appraisal record found for this goal.");
        setShowErrorDialog(true);
        setShowConfirmDialog(false);
        return;
      }

      // Update the record with manager rating data - THIS IS THE FIX
      const { error: updateError } = await supabase
        .from('performance_appraisals')
        .update({
          status: "manager_rated",
          manager_rating: managerRatingValue, // Convert string to number
          manager_comments: comments, // Save the manager comments
          manager_review_date: new Date().toISOString(),
          year: activeYear
        })
        .eq('id', existingAppraisal.id);

      if (updateError) {
        console.error("Error saving manager rating:", updateError);
        setErrorMessage(`Failed to save manager rating: ${updateError.message}`);
        setShowErrorDialog(true);
        setShowConfirmDialog(false);
        return;
      }

      // Success flow
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);
      onSaveSuccess(goalId);
    } catch (error) {
      console.error("Error saving manager rating:", error);
      setErrorMessage("An unexpected error occurred while saving.");
      setShowErrorDialog(true);
      setShowConfirmDialog(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    onClose();
    setComments("");
    setSelfComments("");
    setSelfRatingValue("");
  };

  const handleErrorDialogClose = () => {
    setShowErrorDialog(false);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-white border-primary/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-black">Edit manager rating comments for goal</DialogTitle>
            <DialogDescription className="text-gray-600">
              Goal: {goalTitle}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {targetValue && (
              <div>
                <label className="text-sm font-medium text-black mb-1 block">Target Value</label>
                <div className="text-sm text-gray-700 mb-2 p-2 bg-gray-50 rounded border">
                  {targetValue}
                </div>
              </div>
            )}
            
            {selfRatingValue && (
              <div>
                <label className="text-sm font-medium text-black mb-1 block">Team Member's Self Rating</label>
                <div className="text-sm text-gray-700 mb-2 p-2 bg-gray-50 rounded border">
                  {selfRatingValue}
                </div>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-black mb-2 block">Team member comments</label>
              <Textarea
                value={selfComments}
                readOnly
                className="w-full h-[80px] bg-gray-50 border-gray-300 text-black resize-none overflow-y-auto"
                placeholder="No comments from team member"
                tabIndex={-1}
              />
            </div>

            {managerRating && (
              <div>
                <label className="text-sm font-medium text-black mb-1 block">Manager Rating</label>
                <div className="text-sm text-gray-700 mb-2 p-2 bg-gray-50 rounded border">
                  {getManagerRatingLabel(managerRating)}
                </div>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-black mb-2 block">Manager comments</label>
              <Textarea
                ref={managerCommentsRef}
                placeholder="Enter your manager rating comments..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full h-[80px] bg-white border-gray-300 text-black resize-none overflow-y-auto"
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={onClose}
              className="border-gray-400 text-black hover:bg-gray-100"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-white border-primary/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">Confirm Save</AlertDialogTitle>
            <AlertDialogDescription className="text-red-700">
              Are you sure you want to save your manager rating comments for this goal? No changes can be made after saving.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => setShowConfirmDialog(false)}
              className="border-gray-400 text-black hover:bg-gray-100"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmSave}
              disabled={isSaving}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              {isSaving ? "Saving..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <AlertDialogContent className="bg-white border-red-500/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Error</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-700">
              {errorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={handleErrorDialogClose}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent className="bg-white border-green-500/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-green-600">Success</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-700">
              Manager rating for the goal has been successfully saved!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={handleSuccessDialogClose}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EditManagerRatingDialog;
